/**
 * Higher-Order Component that handles invalid route redirects
 * Wraps a component and redirects to a fallback route when an invalid route is detected
 */
import { PageSlugs } from "../../add-element/types";
import { RouteInfo, useRouteInfo, useRouterNavigation } from "./routingHooks";
import React, { ComponentType, useEffect } from "react";

interface WithInvalidRouteRedirectOptions {
  redirectTo?: string;
  /** Inject route info as a prop to the wrapped component. Defaults to false. */
  injectRoute?: boolean;
  /**
   * Function to determine redirect route dynamically based on props and route info.
   * Takes precedence over redirectTo if provided.
   */
  getRedirectRoute?: (props: any, route: RouteInfo) => string;
}

export function withInvalidRouteRedirect<P extends Record<string, any>>(
  Component: ComponentType<P>,
  options: WithInvalidRouteRedirectOptions = {},
): ComponentType<Omit<P, "route">> {
  const {
    redirectTo = PageSlugs.Home,
    injectRoute = false,
    getRedirectRoute,
  } = options;

  const WrappedComponent = (props: any) => {
    const route = useRouteInfo();
    const navigateTo = useRouterNavigation();

    useEffect(() => {
      if (!route.isValidRoute && route.path !== "/") {
        const targetRoute = getRedirectRoute
          ? getRedirectRoute(props, route)
          : redirectTo;

        if (targetRoute) {
          navigateTo(targetRoute, {}, { replace: true });
        }
      }
    }, [navigateTo, route, props]);

    // Conditionally inject route prop if requested
    const componentProps = injectRoute ? { ...props, route } : props;

    return <Component {...componentProps} />;
  };

  return WrappedComponent;
}
